import { trackDownload, reportProblem } from "../lib/track";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { guide } from "../world/catalog";
import { Link, useSearchParams } from "react-router-dom";
import { imageIds, SUBJECT_PROFILES, sectionItems, sectionMarks, uasaPapersFor, uasaSubjectName, type AiQuestion, type UasaFormat, type PaperBlueprint, type PaperSettings, type UasaBlueprint, type UasaQuestion } from "@jomcerdik/shared";
import { Art } from "../ui/Art";
import { Banner, pastel } from "../ui/kit";
import { Alert, Button } from "../components/Form";
import { useMe } from "../hooks/useMe";
import { api, ApiError, errorMessage, waitForExam } from "../lib/api";
import { CreditLine, DskpGenerator } from "./DskpGenerator";
import { locale, t, tk } from "../lib/i18n";

type Options = { credits: number | null; unlimited: boolean };
type ExamRow = { id: string; status: string; failure_reason: string | null; settings_json: PaperSettings; created_at: string };

const FAIL: Record<string, string> = {
  validation_failed: tk("Kertas belum lulus semakan kandungan atau tulisan. Kredit anda tidak ditolak."),
  image_generation_failed: tk("Gambar belum berjaya dijana. Kertas belum disiapkan dan kredit anda tidak ditolak."),
  provider_error: tk("Perkhidmatan AI tidak dapat dihubungi. Kredit anda tidak ditolak."),
  internal_error: tk("Ralat sistem. Kredit anda tidak ditolak."),
};

type View = "jana" | "latihan" | "kertas" | "keputusan";
const VIEWS: { k: View; label: string; text: string; art: string }[] = [
  { k: "jana", label: tk("Jana Kertas"), text: tk("Pilih tahun, subjek, format dan topik."), art: "mascot/boy-think" },
  { k: "latihan", label: tk("Latihan Dalam Talian"), text: tk("Pratonton, jawab dan semak jawapan terus."), art: "mascot/girl-reading" },
  { k: "kertas", label: tk("Kertas Saya"), text: tk("Muat turun PDF kertas dan skema."), art: "mascot/boy-reading" },
  { k: "keputusan", label: tk("Keputusan dan Laporan"), text: tk("Markah, bintang dan kertas yang perlu diulang."), art: "mascot/girl-cheer" },
];

export function UasaPage() {
  const { me, loading } = useMe();
  const [qs] = useSearchParams();
  const [view, setView] = useState<View>(((k) => (VIEWS.some((v) => v.k === k) ? k : "jana") as View)(qs.get("tab")));
  return (
    <>
      <Banner art="cover/uasa-hero" kicker="Jom" title="UASA" tagline={t("UASA lebih mudah, lebih teratur")} />
      <p className="mb-4 text-center text-base text-slate-600">{t("Pentaksiran Tahun 1–3 (PBD) dan set format UASA Tahun 4–6, atau latihan ikut DSKP.")}</p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" role="tablist" aria-label={t("Bahagian UASA")}>
        {VIEWS.map((v, n) => (
          <button key={v.k} type="button" role="tab" aria-selected={view === v.k} onClick={() => setView(v.k)}
            className={`${pastel(n)} flex min-h-40 flex-col items-center justify-center gap-1 rounded-3xl p-3 text-center shadow-sm transition active:scale-95 ${view === v.k ? "ring-4 ring-brand" : ""}`}>
            <Art id={v.art} alt="" className="h-20 w-20" />
            <span className="text-lg font-black leading-tight text-slate-900">{t(v.label)}</span>
            <span className="text-xs text-slate-600">{t(v.text)}</span>
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <p className="text-sm text-muted">{t("Memuatkan…")}</p>
        ) : !me ? (
          <Alert kind="info">
            <Link to="/login?next=/uasa" className="underline">{t("Log masuk")}</Link> {t("untuk menjana kertas.")}
          </Alert>
        ) : !me.licenseActive ? (
          <Alert kind="info">
            {t("Penjana kertas untuk pemegang lesen.")} <Link to="/beli" className="underline">{t("Dapatkan lesen")}</Link>.
          </Alert>
        ) : (
          <Generator view={view} setView={setView} />
        )}
      </div>
    </>
  );
}

type Version = { versionId: string; level: string; year: number; subject: string; subjectName: string; stream: string; language: string };
/** One student-facing variant per subject: Malay-medium where both exist, SK stream for BM. */
const primary = (v: Version) => v.level === "rendah" && v.stream !== "SJK" && !(v.subject === "matematik" && v.language === "en");

const KIND: Record<string, string> = { objektif: tk("objektif"), jawapan: tk("jawapan pendek"), struktur: tk("struktur"), susun: tk("susun ayat"), isian: tk("isian"), padan: tk("padanan"), tanda: tk("tanda ✓/×") };
/** "A 20 objektif · B susun ayat, isian, struktur ×2 · C isian (pilih 1)" */
function describe(f: UasaFormat): string {
  return f.sections.map((s) => {
    const items = sectionItems(s);
    const groups: [string, number][] = [];
    for (const it of items) {
      const k = KIND[it.kind] ? t(KIND[it.kind]!) : it.kind;
      const last = groups[groups.length - 1];
      if (last && last[0] === k) last[1]++; else groups.push([k, 1]);
    }
    const body = groups.map(([k, n]) => (n > 1 ? `${n} ${k}` : k)).join(", ");
    return `${s.id}: ${body}${s.chooseOne ? ` ${t("(pilih 1)")}` : ""} [${sectionMarks(s)}m]`;
  }).join(" · ") + ` · ${t("{m} markah, {d} minit", { m: f.totalMarks, d: f.durationMinutes })}${f.bilingual ? ` · ${t("dwibahasa")}` : ""}`;
}

const PAGE = 6;
const resultOf = (id: string): { score: number; total: number; at: string } | null => { try { return JSON.parse(localStorage.getItem(`uasa.result.${id}`) ?? "null"); } catch { return null; } };
const paperTitle = (s: PaperSettings) => s.mode === "uasa"
  ? `${t(SUBJECT_PROFILES[s.subject ?? ""]?.nameMs ?? uasaSubjectName(s.subject ?? ""))} ${t("Tahun {n}", { n: s.year })}`
  : s.mode === "dskp"
    ? `${t(SUBJECT_PROFILES[s.subject ?? ""]?.nameMs ?? s.subject ?? "")} ${s.level === "menengah" ? t("Tingkatan {n}", { n: s.year }) : t("Tahun {n}", { n: s.year })}`
    : `${t("Matematik")} ${t("Tahun {n}", { n: s.year })}`;

function Generator({ view, setView }: { view: View; setView: (v: View) => void }) {
  const [tab, setTab] = useState<"uasa" | "dskp">("uasa");
  const [opts, setOpts] = useState<Options | null>(null);
  const [exams, setExams] = useState<ExamRow[]>([]);
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    api<Options>("/api/exams/options").then(setOpts).catch(() => undefined);
    api<{ exams: ExamRow[] }>("/api/exams").then((r) => setExams(r.exams)).catch(() => undefined);
  }, []);
  useEffect(load, [load]);
  // Papers are generated server-side; refresh the list while any is still running.
  const running = exams.some((x) => x.status !== "ready" && x.status !== "failed");
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      api<{ exams: ExamRow[] }>("/api/exams").then((r) => setExams(r.exams)).catch(() => undefined);
    }, 8000);
    return () => clearInterval(id);
  }, [running]);

  const ready = exams.filter((x) => x.status === "ready");
  const pages = Math.max(1, Math.ceil(exams.length / PAGE));
  const shown = exams.slice(page * PAGE, page * PAGE + PAGE);

  if (view === "jana") {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div role="tablist" className="flex flex-wrap gap-2">
          {([["uasa", tk("Format UASA")], ["dskp", tk("Latihan ikut DSKP")]] as const).map(([k, l]) => (
            <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`min-h-11 rounded-full px-5 text-base font-extrabold ${tab === k ? "bg-brand text-white shadow-sm" : "pastel-blue text-slate-800"}`}>
              {t(l)}
            </button>
          ))}
        </div>
        {tab === "dskp" ? (
          <DskpGenerator credits={opts?.credits ?? null} unlimited={opts?.unlimited ?? false} onDone={load} />
        ) : (
          <UasaFormatForm credits={opts?.credits ?? null} unlimited={opts?.unlimited ?? false} onDone={load} />
        )}
        {ready.length > 0 && <p className="text-sm text-slate-600">{t("Kertas siap boleh dijawab dalam talian di bahagian Latihan Dalam Talian.")} <button type="button" className="font-bold text-brand underline" onClick={() => setView("latihan")}>{t("Buka latihan")}</button></p>}
      </div>
    );
  }

  if (view === "latihan") {
    return (
      <section>
        <h2 className="mb-3 text-2xl font-black text-slate-900">{t("Latihan Dalam Talian")}</h2>
        {ready.length === 0 && <p className="text-sm text-muted">{t("Belum ada kertas siap. Jana kertas dahulu.")}</p>}
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ready.map((x, n) => {
            const r = resultOf(x.id);
            return (
              <li key={x.id} className={`${pastel(n)} rounded-3xl p-4`}>
                <p className="text-lg font-black text-slate-900">{paperTitle(x.settings_json)}</p>
                <p className="text-xs text-slate-600">{new Date(x.created_at).toLocaleDateString(locale(), { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium" })}{r ? ` · ${r.score}/${r.total}` : ""}</p>
                <Link to={`/uasa/kertas/${x.id}`} className="mt-3 inline-flex min-h-11 items-center rounded-full bg-brand px-5 text-sm font-extrabold text-white">{r ? t("Cuba lagi") : t("Mula latihan")}</Link>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  if (view === "keputusan") {
    const done = ready.map((x) => ({ x, r: resultOf(x.id) })).filter((d) => d.r);
    const avg = done.length ? Math.round((100 * done.reduce((n, d) => n + d.r!.score / d.r!.total, 0)) / done.length) : 0;
    return (
      <section>
        <h2 className="mb-3 text-2xl font-black text-slate-900">{t("Keputusan dan Laporan")}</h2>
        {done.length === 0 ? <p className="text-sm text-muted">{t("Belum ada keputusan. Selesaikan satu latihan dalam talian.")}</p> : (
          <>
            <div className="pastel-green mb-4 rounded-3xl p-4"><p className="text-sm text-slate-600">{t("Purata markah")}</p><p className="text-4xl font-black text-slate-900">{avg}%</p><p className="text-sm text-slate-600">{t("{n} latihan diselesaikan", { n: done.length })}</p></div>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {done.map(({ x, r }, n) => (
                <li key={x.id} className={`${pastel(n + 1)} rounded-3xl p-4`}>
                  <p className="font-black text-slate-900">{paperTitle(x.settings_json)}</p>
                  <p className="text-3xl font-black text-slate-900">{r!.score}/{r!.total}</p>
                  <div className="my-1 h-2 overflow-hidden rounded-full bg-black/10"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round((100 * r!.score) / r!.total)}%` }} /></div>
                  <Link to={`/uasa/kertas/${x.id}`} className="text-sm font-bold text-brand underline">{r!.score / r!.total < 0.6 ? t("Perlu diulang") : t("Lihat semakan")}</Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    );
  }

  return (
    <section>
      <h2 className="mb-3 text-2xl font-black text-slate-900">{t("Kertas saya")}</h2>
      {exams.length === 0 && <p className="text-sm text-muted">{t("Belum ada kertas.")}</p>}
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((x, n) => <ExamItem key={x.id} x={x} tone={pastel(n)} />)}
      </ul>
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="min-h-11 rounded-full bg-card px-5 font-bold ring-1 ring-black/10 disabled:opacity-40">‹</button>
          <span className="text-sm font-bold text-slate-700">{page + 1} / {pages}</span>
          <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="min-h-11 rounded-full bg-card px-5 font-bold ring-1 ring-black/10 disabled:opacity-40">›</button>
        </div>
      )}
    </section>
  );
}

function UasaFormatForm({ credits, unlimited, onDone }: { credits: number | null; unlimited: boolean; onDone: () => void }) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [subject, setSubject] = useState("sains");
  const [year, setYear] = useState(6);
  const [paper, setPaper] = useState<1 | 2>(1);
  const [units, setUnits] = useState<string[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api<{ versions: Version[] }>("/api/exams/catalogue").then((r) => setVersions(r.versions)).catch((e) => setError(errorMessage(e)));
  }, []);
  const yearVersions = versions.filter((v) => primary(v) && v.year === year);
  const subjects = [...new Set(yearVersions.map((v) => v.subject))];
  const version = yearVersions.find((v) => v.subject === subject);
  const papers = uasaPapersFor(subject, year);
  const format = papers.find((f) => f.paper === paper) ?? papers[0];
  useEffect(() => {
    if (subjects.length && !subjects.includes(subject)) setSubject(subjects[0]!);
  }, [subjects.join(","), subject]);
  useEffect(() => { if (!papers.some((f) => f.paper === paper)) setPaper(1); }, [subject, year]);
  useEffect(() => {
    setUnits([]); setPicked([]);
    if (!version) return;
    api<{ standards: { topic: string | null; skCode: string }[] }>(`/api/exams/standards/${version.versionId}`)
      .then((r) => { const u = [...new Set(r.standards.map((s) => s.topic ?? s.skCode))]; setUnits(u); setPicked(u); })
      .catch((e) => setError(errorMessage(e)));
  }, [version?.versionId]);

  const toggle = (u: string) => setPicked((cur) => (cur.includes(u) ? cur.filter((x) => x !== u) : [...cur, u]));
  const nice = (x: string) => (x === x.toUpperCase() ? x.toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase()) : x);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!version) return;
    setBusy(true); setError(null); setNotice(null);
    guide("write", tk("Kertas sedang dijana. Tunggu status sebenar sebelum membuka pratonton."));
    try {
      const r = await api<{ id: string; status: string; failure?: string }>("/api/exams/generate", {
        method: "POST",
        json: { mode: "uasa", versionId: version.versionId, paper: format?.paper ?? 1, ...(picked.length < units.length ? { units: picked } : {}) },
      });
      let done: { status: string; failure?: string } = r;
      if (r.status === "generating") { onDone(); done = await waitForExam(r.id, 8 * 60_000); }
      if (done.status === "ready") { setNotice(t("Kertas siap! Muat turun di senarai 'Kertas saya'.")); guide("celebrate", tk("Kertas siap! Buka kertas yang tersedia dalam senarai.")); }
      else if (done.status === "generating") setNotice(t("Masih dijana (set penuh ambil 2–5 minit). Anda boleh tinggalkan halaman ini."));
      else setError(t(FAIL[done.failure ?? ""] ?? tk("Penjanaan gagal.")));
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5 rounded-[28px] bg-card p-5 shadow-sm ring-1 ring-black/5 sm:p-6">
      <h2 className="text-xl font-black text-slate-900">{year <= 3 ? t("Set latihan pentaksiran Tahun 1–3 (PBD)") : t("Set soalan format UASA")}</h2>
      <CreditLine credits={credits} unlimited={unlimited} />
      <fieldset>
        <legend className="text-base font-black text-slate-900"><span className="mr-2 inline-grid h-7 w-7 place-items-center rounded-full bg-brand text-sm text-white">1</span>{t("Tahun")}</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5, 6].map((y) => (
            <button key={y} type="button" aria-pressed={year === y} onClick={() => setYear(y)}
              className={`min-h-11 rounded-full px-5 text-sm font-extrabold transition active:scale-95 ${year === y ? "bg-brand text-white shadow-sm" : "pastel-green text-slate-800"}`}>
              {t("Tahun {n}", { n: y })}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-base font-black text-slate-900"><span className="mr-2 inline-grid h-7 w-7 place-items-center rounded-full bg-brand text-sm text-white">2</span>{t("Subjek")}</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {subjects.length === 0 && <span className="text-sm text-muted">{t("Tiada subjek untuk tahun ini lagi.")}</span>}
          {subjects.map((s) => (
            <button key={s} type="button" aria-pressed={subject === s} onClick={() => setSubject(s)}
              className={`min-h-11 rounded-full px-5 text-sm font-extrabold transition active:scale-95 ${subject === s ? "bg-brand text-white shadow-sm" : "pastel-blue text-slate-800"}`}>
              {t(SUBJECT_PROFILES[s]?.nameMs ?? uasaSubjectName(s))}
            </button>
          ))}
        </div>
      </fieldset>
      {papers.length > 1 && (
        <fieldset>
          <legend className="text-base font-black text-slate-900"><span className="mr-2 inline-grid h-7 w-7 place-items-center rounded-full bg-brand text-sm text-white">3</span>{t("Kertas")}</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {papers.map((f) => (
              <button key={f.paper} type="button" aria-pressed={format?.paper === f.paper} onClick={() => setPaper(f.paper)}
                className={`min-h-11 rounded-full px-5 text-sm font-extrabold transition active:scale-95 ${format?.paper === f.paper ? "bg-brand text-white shadow-sm" : "pastel-pink text-slate-800"}`}>
                {f.paper === 1 ? (f.paperName?.includes("Penulisan") ? t("Kertas 2 - Penulisan") : f.paperName ? t("Kertas 1 - Pemahaman") : t("Kertas 1")) : t("Kertas 2 - Penulisan")}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {units.length > 0 && (
        <fieldset>
          <legend className="text-base font-black text-slate-900"><span className="mr-2 inline-grid h-7 w-7 place-items-center rounded-full bg-brand text-sm text-white">4</span>{t("Unit yang diuji ({n}/{max})", { n: picked.length, max: units.length })}</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {units.map((u) => (
              <button key={u} type="button" aria-pressed={picked.includes(u)} onClick={() => toggle(u)}
                className={`min-h-9 rounded-full px-3 text-xs font-bold ${picked.includes(u) ? "bg-emerald-500 text-white" : "pastel-yellow text-slate-700"}`}>
                {nice(u)}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {year <= 3 && <Alert kind="info">{t("Tahun 1–3 menggunakan pentaksiran bilik darjah (PBD), bukan format UASA rasmi KPM. Semak kesesuaian soalan sebelum digunakan.")}</Alert>}
      {format && <p className="text-xs text-muted">{describe(format)}</p>}
      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}
      <Button type="submit" busy={busy} disabled={!version || !format || picked.length === 0 || (!unlimited && (credits ?? 0) < 1)} className="w-full">
        {t("Jana set soalan")}
      </Button>
      {busy && <p className="text-xs text-muted" role="status">{t("Masih dijana. Anda boleh tinggalkan halaman ini; kertas akan muncul dalam senarai.")}</p>}
    </form>
  );
}

function ExamItem({ x, tone = "" }: { x: ExamRow; tone?: string }) {
  const [busy, setBusy] = useState<"paper" | "scheme" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const s = x.settings_json;

  async function download(kind: "paper" | "scheme") {
    setBusy(kind);
    setError(null);
    try {
      const { exam } = await api<{ exam: { paper_json: { kind?: "uasa"; blueprint: unknown; questions: unknown[] } } }>(`/api/exams/${x.id}`);
      const pdf = await import("@jomcerdik/shared/pdf"); // loaded on demand
      const meta = { brand: "JomCerdik", official: false, generatedAt: new Date(x.created_at), reference: `JC-${x.id.slice(0, 8).toUpperCase()}` };
      const pj = exam.paper_json;
      const images: Record<string, Uint8Array> = {};
      if (pj.kind === "uasa" && kind === "paper") {
        // Illustrations generated for this paper (if any) are fetched first and embedded in the PDF.
        await Promise.all(imageIds(pj.questions as UasaQuestion[]).map(async (key) => {
          const r = await fetch(`/api/exams/${x.id}/images/${key}`, { credentials: "same-origin" }).catch(() => null);
          if (!r?.ok) throw new ApiError(503, "image_unavailable");
          images[key] = new Uint8Array(await r.arrayBuffer());
        }));
      }
      const bytes = pj.kind === "uasa"
        ? await (kind === "paper"
          ? pdf.renderUasaPaper(pj.blueprint as UasaBlueprint, pj.questions as UasaQuestion[], meta, images)
          : pdf.renderUasaScheme(pj.blueprint as UasaBlueprint, pj.questions as UasaQuestion[], meta))
        : await (kind === "paper" ? pdf.renderQuestionPaper : pdf.renderAnswerScheme)(pj.blueprint as PaperBlueprint, s, pj.questions as AiQuestion[], meta);
      const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${meta.reference}-${kind === "paper" ? "soalan" : "skema"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      trackDownload({ subject: s.subject ?? (s.mode === "official" || s.mode === "custom" ? "matematik" : undefined), file: kind === "paper" ? "soalan" : "skema", exam: x.id });
    } catch (e) {
      void reportProblem("download_failed", `${kind === "paper" ? "Kertas soalan" : "Skema"}: ${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`, {
        exam: x.id, subject: s.subject ?? null, year: s.year ?? null, mode: s.mode ?? null, status: e instanceof ApiError ? e.status : null,
        stack: e instanceof Error && e.stack ? e.stack.slice(0, 1500) : null,
      });
      // Network/API errors come from api(); anything else is the PDF builder failing on this paper.
      setError(e instanceof ApiError || (e instanceof TypeError && /fetch|import/i.test(e.message)) ? errorMessage(e) : t("PDF tidak dapat dibina untuk kertas ini. Sila laporkan kepada admin."));
      console.error(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className={`${tone} rounded-3xl p-4 text-sm`}>
      <p className="font-medium">
        {s.mode === "uasa"
          ? `${t(SUBJECT_PROFILES[s.subject ?? ""]?.nameMs ?? uasaSubjectName(s.subject ?? ""))}${s.paper && uasaPapersFor(s.subject ?? "", s.year).length > 1 ? ` ${t("Kertas {n}", { n: s.paper })}` : ""} ${t("Tahun {n}", { n: s.year })} · ${t("Set format UASA")}`
          : s.mode === "dskp"
            ? `${t(SUBJECT_PROFILES[s.subject ?? ""]?.nameMs ?? s.subject ?? "")} ${s.level === "menengah" ? t("Tingkatan {n}", { n: s.year }) : t("Tahun {n}", { n: s.year })} · ${t("DSKP ({n} soalan)", { n: s.questionCount ?? 0 })}`
            : `${t("Matematik")} ${t("Tahun {n}", { n: s.year })} · ${s.mode === "official" ? t("Format UASA (lama)") : t("Latihan ({n} soalan)", { n: s.questionCount ?? 0 })}`}
      </p>
      <p className="text-xs text-muted">
        {s.mode === "dskp" ? `SP ${(s.spCodes ?? []).join(", ")}` : (s.topics ?? []).join(", ")} · {new Date(x.created_at).toLocaleString(locale(), { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" })}
      </p>
      {x.status === "ready" ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button onClick={() => download("paper")} busy={busy === "paper"}>{t("Kertas soalan")}</Button>
          <Button variant="secondary" onClick={() => download("scheme")} busy={busy === "scheme"}>{t("Skema jawapan")}</Button>
          <Link to={`/uasa/kertas/${x.id}`} className="inline-flex min-h-10 items-center rounded-full bg-white/80 px-4 text-sm font-bold text-brand ring-1 ring-brand/20">{t("Latihan dalam talian")}</Link>
        </div>
      ) : (
        <p className="mt-1 text-xs text-red-700">{x.status === "failed" ? t(FAIL[x.failure_reason ?? ""] ?? "Gagal") : t("Sedang dijana…")}</p>
      )}
      {error && <div className="mt-2"><Alert kind="error">{error}</Alert></div>}
    </li>
  );
}


